import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import '../styles/Auth.css';

function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [otp, setOtp] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/email/verify-otp', { email, otp });
      navigate('/login?success=Email verified! You can now sign in.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setResending(true);
    setResent(false);
    try {
      await api.post('/auth/email/send-verification-otp', { email });
      setMessage('A new verification code has been sent to your inbox.');
      setResent(true);
      setOtp('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="auth-page">
      {/* ── Sidebar ── */}
      <div className="auth-sidebar">
        <Link to="/" className="auth-sidebar__logo">
          Postrify <span className="auth-sidebar__dot" />
        </Link>
        <div className="auth-sidebar__body">
          <h2 className="auth-sidebar__headline">
            Almost<br /><span>there!</span>
          </h2>
          <p className="auth-sidebar__desc">
            One quick step — verify your email address and your
            Postrify account will be ready to go.
          </p>
          <ul className="auth-sidebar__features">
            {['Check your inbox for the code', 'Code valid for 10 minutes', 'Resend if you don\'t see it', 'Check spam just in case'].map((f) => (
              <li className="auth-sidebar__feature" key={f}>
                <span className="auth-sidebar__feature-icon">✓</span> {f}
              </li>
            ))}
          </ul>
        </div>
        <p className="auth-sidebar__footer">© 2024 Postrify</p>
      </div>

      {/* ── Form Panel ── */}
      <div className="auth-panel">
        <div className="auth-form-wrap">
          <Link to="/register" className="auth-form-wrap__back">← Back to register</Link>

          {/* Icon */}
          <div style={{
            width:'56px', height:'56px', borderRadius:'16px', background:'#e0e7ff',
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:'28px', marginBottom:'20px',
          }}>
            📧
          </div>

          <h1 className="auth-form-wrap__title">Verify your email</h1>
          <p className="auth-form-wrap__sub">
            {email
              ? <>We sent a 6-digit code to <strong style={{ color:'#16152e' }}>{email}</strong></>
              : 'Enter your email and the 6-digit code to activate your account.'}
          </p>

          {error   && <div className="ps-alert ps-alert--error">{error}</div>}
          {message && <div className="ps-alert ps-alert--success">{message}</div>}

          <form onSubmit={handleVerify}>
            {/* Show email field only if not pre-filled */}
            {!searchParams.get('email') && (
              <div className="ps-field">
                <label className="ps-label" htmlFor="email">Email Address</label>
                <input className="ps-input" type="email" id="email"
                  placeholder="you@example.com" autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
            )}

            <div className="ps-field">
              <label className="ps-label" htmlFor="otp">Verification Code</label>
              <input className="ps-input" type="text" id="otp"
                inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                placeholder="000000" autoComplete="one-time-code"
                style={{ fontSize:'28px', letterSpacing:'0.4em', textAlign:'center', fontWeight:700, paddingTop:'14px', paddingBottom:'14px' }}
                value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} required />
              <p className="ps-hint">Enter the 6-digit code from your email. Check your spam folder if needed.</p>
            </div>

            <button type="submit" className="ps-btn ps-btn--primary ps-btn--full ps-btn--lg" disabled={loading}>
              {loading ? 'Verifying…' : 'Verify email →'}
            </button>
          </form>

          {/* Resend */}
          <div style={{
            marginTop:'20px', padding:'16px', background:'#f8f8fc',
            borderRadius:'10px', border:'1px solid #e4e4ef', textAlign:'center',
          }}>
            <p style={{ fontSize:'13px', color:'#8b8aab', marginBottom:'10px' }}>
              {resent ? 'Code resent! Check your inbox.' : 'Didn\'t receive the code?'}
            </p>
            <button type="button"
              onClick={handleResend} disabled={resending}
              className="ps-btn ps-btn--secondary ps-btn--sm">
              {resending ? 'Resending…' : '↻ Resend code'}
            </button>
          </div>

          <p className="auth-bottom">
            Wrong account? <Link to="/login">Sign in with another</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default VerifyEmail;
